import { httpClient } from '@/lib/httpClient';

export enum VisionStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface SearchFilter {
  pairsWith: boolean;
  productName: string;
  productId: string;
  productSku: string;
  unique: boolean;
  availableProduct: boolean;
  visionCategory: string;
}

export interface ProductsQueryFilterDto extends Partial<SearchFilter> {
  page: number;
  limit: number;
}

export interface SearchDto {
  userId: string | null;
  sessionId: string;
}

export interface SearchParams {
  query: string;
  page: number;
  limit: number;
}

export interface VisionOutput {
  product_attributes: Record<string, any>;
  additional_seo_translations: any[];
}

export interface FormattedSeo {
  name: string;
  description: string;
  seoDescription: string;
  tags: string;
  images: {
    url: string;
    content: string;
  }[];
}

export interface Product {
  id: number;
  productId: string;
  sku: string;
  image1: string;
  image2: string;
  image3: string;
  image4: string;
  image5: string;
  image6: string;
  image7: string;
  image8: string;
  image9: string;
  image10: string;
  name: string;
  title: string;
  price: string;
  stock: boolean;
  isActive: boolean;
  salePrice: string;
  sale: boolean;
  link: string;
  formattedSeo: FormattedSeo;
  visionCategoryName: string;
  visionOutput: VisionOutput;
  visionStatus: VisionStatus;
  shopTheLook: null | string;
  merchandisingCollectionSlices: null | string;
  fashionaiVideo: string | null;
}

export interface SearchProduct {
  id: number;
  productId: string;
  name: string;
  image1: string;
  price: string;
  salePrice: string;
}

export interface ProductsResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: Product[];
}

export interface SearchProductsResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: SearchProduct[];
}

export interface ImageAltTextName {
  content: string;
  url: string;
}

export interface VisionProductAttributes {
  [key: string]: {
    id: string;
    property: string;
    name: string;
    order?: number;
  }[];
}

export interface VisionProductsResponse {
  name: string;
  productId: string;
  productAttributes: VisionProductAttributes;
}

export interface PairsWith {
  id: number;
  image1: string;
  image2: string;
  link: string;
  name: string;
  productId: string;
  price: string;
  salePrice: string;
}

export interface VisionProductResponse {
  id: number;
  name: string;
  productId: string;
  images: string[];
  visionCategory: string;
  link: string;
  price: string;
  salePrice: string;
  productAttributes: VisionProductAttributes;
  pairsWith: PairsWith[];
}

export interface VisionAttributresParams {
  visionCategory: string;
  property: string;
  name: string;
}

export type VisionAttributresResponse = string[];

export interface UpdateProductAttributeDto {
  propertyKey: string;
  property: string;
  propertyName: string;
}

export interface UpdateVisionAttributeDto {
  propertyKey: string;
  propertyName: string;
  propertyValue: string;
}

export interface UpdateVisionAttributeResponse {
  id: string;
  property: string;
  name: string;
}

export enum Format {
  CSV = 'csv',
  XLSX = 'xlsx',
}

export interface DownloadProductSeoCSVDto {
  language: string;
  format: Format;
}

export interface DownloadProductSeoCSVResponse {
  filename: string;
  url: string;
}

export enum UpdatePairsWithAction {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export interface UpdatePairsWithDto {
  action: UpdatePairsWithAction;
  productId: string;
  pairsWithId: string;
}

export interface UpdatePairsWithResponse {
  shopTheLook: string;
}

export interface UpdateProductSeoDto {
  productId: string;
  name?: string;
  description?: string;
  seoDescription?: string;
  tags?: string;
  imageUrl?: string;
  imageContent?: string;
}

export interface UpdateProductSeoResponse {
  id: number;
  productId: string;
  sku: string;
  formattedSeo: FormattedSeo;
}

export interface UpdateToneOfVoiceDto {
  productId: string;
  property: string;
  name: string;
}

export interface UpdateToneOfVoiceResponse {
  id: number;
  productId: string;
  sku: string;
  formattedSeo: FormattedSeo;
}

export interface RerunProductDto {
  catalogId: number;
}

export interface DownloadResponse {
  fileKey: string;
  url: string;
}

class ProductsService {
  constructor() {}

  async getProducts(productsQueryFilterDto: ProductsQueryFilterDto) {
    const { data } = await httpClient.get<ProductsResponse>('/v1/products', {
      params: productsQueryFilterDto,
    });

    return data;
  }

  async getProduct(productId: number) {
    const { data } = await httpClient.get<Product>(
      `/v1/products/find/${productId}`,
    );

    return data;
  }

  async getVisionProducts() {
    const { data } = await httpClient.get<VisionProductsResponse[]>(
      `/v1/products/get-vision-products`,
    );

    return data;
  }

  async searchProducts(searchDto: SearchDto, searchParams: SearchParams) {
    const { data } = await httpClient.post<SearchProductsResponse>(
      `/v1/products/search`,
      searchDto,
      {
        params: searchParams,
      },
    );

    return data;
  }

  async searchAlternativeProducts(
    searchDto: SearchDto,
    searchParams: SearchParams,
  ) {
    const { data } = await httpClient.post<SearchProductsResponse>(
      `/v1/products/search/alternative`,
      searchDto,
      {
        params: searchParams,
      },
    );

    return data;
  }

  async getVisionProduct(productId: number) {
    const { data } = await httpClient.get<VisionProductResponse>(
      `/v1/products/get-vision-product/${productId}`,
    );

    return data;
  }

  async getVisionAttributes(params: VisionAttributresParams) {
    const { data } = await httpClient.get<VisionAttributresResponse>(
      '/v1/products/get-vision-attributes',
      {
        params,
      },
    );

    return data;
  }

  async updateProductAttribute(
    productId: number,
    updateProductAttributeDto: UpdateProductAttributeDto,
  ) {
    const { data } = await httpClient.patch<Product>(
      `/v1/products/update-product-attribute/${productId}`,
      updateProductAttributeDto,
    );

    return data;
  }

  async getProductsRecommendation(
    queryText: string,
    page: number,
    limit: number,
  ) {
    const { data } = await httpClient.get('/v1/products/get-recommendation', {
      params: {
        queryText,
        page,
        limit,
      },
    });

    return data;
  }

  async downloadProductSeoCSV(
    downloadProductSeoCSVDto: DownloadProductSeoCSVDto,
  ) {
    const { data } = await httpClient.post<DownloadProductSeoCSVResponse>(
      '/v1/products/download-product-seo-csv',
      downloadProductSeoCSVDto,
    );

    return data;
  }

  async updatePairsWith(updatePairsWithDto: UpdatePairsWithDto) {
    const { data } = await httpClient.post<UpdatePairsWithResponse>(
      `/v1/products/update-pairs-with`,
      updatePairsWithDto,
    );

    return data;
  }

  async updateSeo(updateProductSeoDto: UpdateProductSeoDto) {
    const { data } = await httpClient.post<UpdateProductSeoResponse[]>(
      `/v1/products/update/seo`,
      updateProductSeoDto,
    );

    return data;
  }

  async updateToneOfVoice(updateToneOfVoiceDto: UpdateToneOfVoiceDto) {
    const { data } = await httpClient.post<UpdateToneOfVoiceResponse[]>(
      `/v1/products/update/tone-of-voice`,
      updateToneOfVoiceDto,
    );

    return data;
  }

  async rerunProduct(rerunProductDto: RerunProductDto) {
    const { data } = await httpClient.post<boolean>(
      `/v1/products/rerun`,
      rerunProductDto,
    );

    return data;
  }

  async getDownloadUrl(fileKey: string) {
    const { data } = await httpClient.post<DownloadResponse>(
      `/v1/products/download-enrichment`,
      { fileKey },
    );
    return data;
  }

  async recreateProductName(catalogId: number) {
    const { data } = await httpClient.post<{ name: string }>(
      `/v1/products/recreate-product-name/${catalogId}`,
      {},
      { timeout: 120000 }, // 2 minutos
    );

    return data;
  }
}

export default new ProductsService();
